/**
 * inlineEditController — the governed seam between an editable CRM grid
 * cell and the approval queue.
 *
 * WHY: the auditor's #1 finding was inline editing that LOOKED editable
 * but never persisted — an input whose blur handler went nowhere. This
 * module closes that gap without adding a feature: every cell edit is
 * validated, compiled into the SAME old→new diff shape the approval
 * cards render (delegating to the existing pure core in
 * `inlineEditDiff.ts`), and committed through an injected `enqueue`
 * (the store's existing queueApproval path) — NEVER a direct store
 * write from here.
 *
 * Design contract (mirrors inlineEditDiff.ts):
 * - PURE except for the injected `enqueue`. No store access, no IO of
 *   its own — tests drive it with an in-memory fake.
 * - A no-op edit (value unchanged, or clearing an already-blank cell)
 *   returns 'noop' and never reaches the queue: a non-change must not
 *   spend operator attention.
 * - Validation failures return 'invalid' with a human-readable error —
 *   the UI renders it as a red border + tooltip, and nothing enqueues.
 * - An enqueue rejection is caught and returned as 'error' — the caller
 *   gets a value, never a throw, so a flaky write can't crash the grid.
 */

import {
  buildCellEditDiff,
  isTrivialEdit,
  type ActionDiff,
  type CellEdit as DiffCellEdit,
} from './inlineEditDiff';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** One inline cell edit as the grid UI sees it: record + field, old → new. */
export type CellEdit = {
  recordId: string;
  field: string;
  oldValue: unknown;
  newValue: unknown;
};

/**
 * The governed diff a committed edit enqueues — the exact `ActionDiff`
 * shape `inlineEditDiff.buildCellEditDiff` produces (recordId, changes,
 * render-ready "attr: from → to" summary).
 */
export type InlineEditDiff = ActionDiff;

/** Declarative per-field validation: type + required + format guards. */
export interface FieldSchema {
  /** Primitive the field stores. */
  type: 'string' | 'number' | 'boolean';
  /** When true, an empty new value is rejected. */
  required?: boolean;
  /** Optional format guard, tested against the stringified value. */
  pattern?: RegExp;
  /** Human label for error messages (falls back to the field name). */
  label?: string;
}

/** What the injected enqueue must resolve with (queueApproval's id). */
export interface QueueResult {
  id: string;
}

/** Outcome of a governed commit attempt. Never throws. */
export interface CommitResult {
  status: 'queued' | 'invalid' | 'noop' | 'error';
  error?: string;
  queueId?: string;
}

/** A cell coordinate in the grid (0-based). */
export type CellPos = { row: number; col: number };

/** Grid dimensions nextCell clamps against. */
export type GridDims = { rows: number; cols: number };

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

/** True for the values we treat as "empty": null/undefined/blank string. */
function isEmpty(v: unknown): boolean {
  return v === null || v === undefined || (typeof v === 'string' && v.trim() === '');
}

/** True when `v` is a number (finite) or a string that parses to one. */
function isNumeric(v: unknown): boolean {
  if (typeof v === 'number') return Number.isFinite(v);
  if (typeof v === 'string') {
    const t = v.trim();
    return t !== '' && Number.isFinite(Number(t));
  }
  return false;
}

/**
 * Validate one cell edit against its field schema: required guard first
 * (an empty required value is invalid regardless of type), then the type
 * guard, then the optional format pattern. Empty AND optional short-
 * circuits to ok — clearing an optional field is always well-formed.
 * Pure; returns a value, never throws.
 */
export function validateCellEdit(
  edit: CellEdit,
  schema: FieldSchema,
): { ok: boolean; error?: string } {
  const label = schema.label ?? edit.field;
  const v = edit.newValue;

  if (isEmpty(v)) {
    return schema.required
      ? { ok: false, error: `${label} is required` }
      : { ok: true };
  }

  switch (schema.type) {
    case 'number':
      if (!isNumeric(v)) return { ok: false, error: `${label} must be a number` };
      break;
    case 'boolean':
      if (typeof v !== 'boolean') {
        return { ok: false, error: `${label} must be true or false` };
      }
      break;
    case 'string':
      if (typeof v !== 'string') return { ok: false, error: `${label} must be text` };
      break;
  }

  if (schema.pattern && !schema.pattern.test(String(v))) {
    return { ok: false, error: `${label} has an invalid format` };
  }
  return { ok: true };
}

// ---------------------------------------------------------------------------
// Diff building — delegates to the existing pure core
// ---------------------------------------------------------------------------

/** Map the grid-facing edit onto inlineEditDiff's CellEdit shape. */
function toDiffEdit(edit: CellEdit): DiffCellEdit {
  return {
    recordId: edit.recordId,
    attr: edit.field,
    from: edit.oldValue,
    to: edit.newValue,
  };
}

/**
 * Compile one cell edit into its governed old→new diff. Thin delegate to
 * `inlineEditDiff.buildCellEditDiff` so the grid and the approval cards
 * can never drift apart on diff shape or summary wording. Pure.
 */
export function buildEditDiff(edit: CellEdit): InlineEditDiff {
  return buildCellEditDiff(toDiffEdit(edit));
}

// ---------------------------------------------------------------------------
// commitCellEdit — the governed commit path
// ---------------------------------------------------------------------------

/** Injected dependencies: the store's existing enqueue, optional schema. */
export interface CommitDeps {
  /** Persist the diff into the approval queue (e.g. via queueApproval). */
  enqueue: (d: InlineEditDiff) => Promise<QueueResult>;
  /** When present, the edit is validated before anything enqueues. */
  schema?: FieldSchema;
}

/**
 * Commit one cell edit through the governed path:
 *
 *   1. no-op guard  — unchanged value (or blank→blank) returns 'noop',
 *                     enqueue is NEVER called;
 *   2. validation   — a schema failure returns 'invalid' + error,
 *                     enqueue is NEVER called;
 *   3. enqueue      — the built diff enters the approval queue; success
 *                     returns 'queued' + queueId, a rejection is caught
 *                     and returned as 'error' (this function never throws).
 */
export async function commitCellEdit(
  edit: CellEdit,
  deps: CommitDeps,
): Promise<CommitResult> {
  if (isTrivialEdit(toDiffEdit(edit))) return { status: 'noop' };

  if (deps.schema) {
    const v = validateCellEdit(edit, deps.schema);
    if (!v.ok) return { status: 'invalid', error: v.error };
  }

  try {
    const res = await deps.enqueue(buildEditDiff(edit));
    return { status: 'queued', queueId: res.id };
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    return { status: 'error', error: msg };
  }
}

// ---------------------------------------------------------------------------
// nextCell — keyboard flow
// ---------------------------------------------------------------------------

const clamp = (n: number, lo: number, hi: number) => Math.min(Math.max(n, lo), hi);

/**
 * Where keyboard commit moves the editing cursor:
 *
 *   Tab      → right (wraps to the next row's first column; clamps at
 *              the last cell of the grid)
 *   ShiftTab → left  (wraps to the previous row's last column; clamps
 *              at the first cell)
 *   Enter    → down one row, same column (clamps at the last row)
 *   Escape   → the origin cell, unchanged (the UI reverts the value)
 *
 * Pure; always returns an in-bounds position for a non-empty grid.
 */
export function nextCell(
  grid: GridDims,
  pos: CellPos,
  key: 'Enter' | 'Tab' | 'Escape' | 'ShiftTab',
): CellPos {
  const lastRow = Math.max(0, grid.rows - 1);
  const lastCol = Math.max(0, grid.cols - 1);
  const row = clamp(pos.row, 0, lastRow);
  const col = clamp(pos.col, 0, lastCol);

  switch (key) {
    case 'Escape':
      return { row: pos.row, col: pos.col };
    case 'Enter':
      return { row: clamp(row + 1, 0, lastRow), col };
    case 'Tab':
      if (col < lastCol) return { row, col: col + 1 };
      return row < lastRow ? { row: row + 1, col: 0 } : { row, col };
    case 'ShiftTab':
      if (col > 0) return { row, col: col - 1 };
      return row > 0 ? { row: row - 1, col: lastCol } : { row, col };
  }
}
